<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { DoubleMaterialityIroImpactType } from '@/__generated__/types';
import useFormatNumber from '@/utils/composables/useFormatNumber';
import { BadgeType } from '@/components/atoms/AtBadge/types';
import AtBadge from '@/components/atoms/AtBadge/AtBadge.vue';
import { useStore } from '../../store';
import OgTable from '../../OgTable.vue';
import {getImpactsData, impactHeaders} from '@/components/pages/Modules/DoubleMateriality/PgResults/PgList/dataGenerators';
import type { OgImpactsRowItem } from '@/components/pages/Modules/DoubleMateriality/PgResults/PgList/types';

const { t } = useI18n();
const store = useStore();
const { formatNumber } = useFormatNumber();

const headers = computed(() =>
  impactHeaders.map((h) => ({ ...h, text: t(h.text) })),
);

const items = computed<OgImpactsRowItem[]>(() => getImpactsData(store.value));
</script>

<template>
  <h3 class="text-lg font-medium mb-3">
    {{ t('Impact materiality') }}
  </h3>
  <OgTable
    :headers="headers"
    :items="items"
    :isRowRemovable="false"
    tableClass="!mb-16"
  >
    <template #item-esrs="row: OgImpactsRowItem">
      <p>{{ row.topic.esrs }} {{ t(row.topic.topic) }}</p>
    </template>
    <template #item-sustainabilityMatter="row: OgImpactsRowItem">
      <p>
        {{ t(row.topic.subSubTopic) || t(row.topic.subTopic) || '' }}
      </p>
    </template>
    <template #item-iro="row: OgImpactsRowItem">
      <p>
        {{ row.iroImpact.impact }}
      </p>
    </template>
    <template #item-iroType="row: OgImpactsRowItem">
      <p>
        <template
          v-if="
            row.iroImpact.type ===
            DoubleMaterialityIroImpactType.ACTUAL_NEGATIVE
          "
        >
          {{ t('Actual negative impact') }}
        </template>
        <template
          v-else-if="
            row.iroImpact.type ===
            DoubleMaterialityIroImpactType.POTENTIAL_NEGATIVE
          "
        >
          {{ t('Potential negative impact') }}
        </template>
        <template
          v-else-if="
            row.iroImpact.type ===
            DoubleMaterialityIroImpactType.ACTUAL_POSITIVE
          "
        >
          {{ t('Actual positive impact') }}
        </template>
        <template
          v-else-if="
            row.iroImpact.type ===
            DoubleMaterialityIroImpactType.POTENTIAL_POSITIVE
          "
        >
          {{ t('Potential positive impact') }}
        </template>
        <template v-else>
          {{ t('n/a') }}
        </template>
      </p>
    </template>
    <template #item-severity="row: OgImpactsRowItem">
      <p>
        {{
          typeof row.severity !== 'number'
            ? t('n/a')
            : formatNumber(row.severity, 0, 2)
        }}
      </p>
    </template>
    <template #item-likelihood="row: OgImpactsRowItem">
      <p>
        {{ row.likelihood ?? t('n/a') }}
      </p>
    </template>
    <template #item-totalScore="row: OgImpactsRowItem">
      <p>
        {{
          typeof row.totalScore !== 'number'
            ? t('n/a')
            : formatNumber(row.totalScore, 0, 2)
        }}
      </p>
    </template>
    <template #item-materiality="row: OgImpactsRowItem">
      <AtBadge
        :type="
          row.materiality === true
            ? BadgeType.SUCCESS
            : row.materiality === false
              ? BadgeType.WARNING
              : BadgeType.DEFAULT
        "
      >
        {{
          row.materiality === true
            ? t('Material')
            : row.materiality === false
              ? t('Not material')
              : t('Start')
        }}
      </AtBadge>
    </template>
  </OgTable>
</template>
